import _ from "lodash";
import { flow, types } from "mobx-state-tree";
import { getEnv } from "~/mobx/utils/getEnv";
import { AppointmentReason } from "./AppointmentReason";
import type {
  AppointmentReasonInstance,
  AppointmentReasonSnapshotIn,
} from "./AppointmentReason";
import type { AxiosResponse } from "axios";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";

export interface AppointmentReasonStoreInstance
  extends Instance<typeof AppointmentReasonStore> {}
export interface AppointmentReasonStoreSnapshotIn
  extends SnapshotIn<typeof AppointmentReasonStore> {}
export interface AppointmentReasonStoreSnapshotOut
  extends SnapshotOut<typeof AppointmentReasonStore> {}

export const AppointmentReasonStore = types
  .model("AppointmentReasonStore", {
    map: types.map(AppointmentReason),
  })
  .actions((self) => {
    return {
      processOne(data: AppointmentReasonSnapshotIn) {
        const existing = self.map.get(data.id);
        if (existing) {
          _.mergeWith(existing, data, (_, next) => {
            if (Array.isArray(next)) return next; // Treat arrays like atoms
          });
          return existing;
        }
        return self.map.put(data as AppointmentReasonSnapshotIn);
      },
    };
  })
  .actions((self) => {
    return {
      processMany(dataList: AppointmentReasonSnapshotIn[]) {
        return dataList.map((data) => {
          return self.processOne(data);
        });
      },
    };
  })
  .actions((self) => {
    return {
      getAppointmentReasonList: flow<
        AppointmentReasonInstance[],
        [string, string | undefined | null]
      >(function* getAppointmentReasonList(
        selectedClinicId: string,
        selectedSpeciesId?: string | null,
      ): any {
        const env = getEnv(self);

        const params = selectedSpeciesId
          ? { speciesId: selectedSpeciesId }
          : {};

        const response: AxiosResponse = yield env.baseHttp.get(
          `clinics/${selectedClinicId}/reasons`,
          {
            params,
          },
        );

        const reasons = response.data.data;

        response.data = self.processMany(reasons);

        return response.data;
      }),
      getAppointmentReason: flow<AppointmentReasonInstance, [string, string]>(
        function* getAppointmentReason(
          id: string,
          selectedClinicId: string,
        ): any {
          const env = getEnv(self);
          const response: AxiosResponse = yield env.baseHttp.get(
            `clinics/${selectedClinicId}/reasons/${id}`,
          );
          const appointmentReason = response.data.data;
          response.data = self.processOne(appointmentReason);
          return response.data;
        },
      ),
    };
  });
